import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useToast,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  Text,
  Flex,
  Spacer,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Spinner,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiRefreshCw, FiCheckCircle, FiXCircle, FiClock } from 'react-icons/fi';

const API_BASE = '/api/v1';

function Monitors() {
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMonitor, setEditingMonitor] = useState(null);
  const [monitorForm, setMonitorForm] = useState({ url: '', description: '' });
  const [monitorToDelete, setMonitorToDelete] = useState(null);
  
  const { isOpen: isMonitorModalOpen, onOpen: onMonitorModalOpen, onClose: onMonitorModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  
  const toast = useToast();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchMonitors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/monitors/`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setMonitors(Array.isArray(data) ? data : data.results || []);
      }
    } catch (e) {
      toast({ title: 'Error fetching monitors', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMonitors();
  }, []);

  const openAddMonitor = () => {
    setEditingMonitor(null);
    setMonitorForm({ url: '', description: '' });
    onMonitorModalOpen();
  };

  const openEditMonitor = (monitor) => {
    setEditingMonitor(monitor);
    setMonitorForm({ url: monitor.url, description: monitor.description });
    onMonitorModalOpen();
  };

  const handleSaveMonitor = async () => {
    const method = editingMonitor ? 'PUT' : 'POST';
    const url = editingMonitor 
      ? `${API_BASE}/monitors/${editingMonitor.id}/` 
      : `${API_BASE}/monitors/`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(monitorForm),
      });
      const data = await res.json();
      if (res.ok) {
        if (editingMonitor) {
          setMonitors(monitors.map(m => m.id === editingMonitor.id ? data : m));
        } else {
          setMonitors([...monitors, data]);
        }
        toast({ title: editingMonitor ? 'Monitor updated' : 'Monitor created', status: 'success', duration: 3000 });
        onMonitorModalClose();
      } else {
        toast({ title: data.error || 'Error saving monitor', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving monitor', status: 'error', duration: 3000 });
    }
  };

  const confirmDeleteMonitor = (monitor) => {
    setMonitorToDelete(monitor);
    onDeleteModalOpen();
  };

  const handleDeleteMonitor = async () => {
    if (!monitorToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/monitors/${monitorToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok || res.status === 204) {
        setMonitors(monitors.filter(m => m.id !== monitorToDelete.id));
        toast({ title: 'Monitor deleted', status: 'success', duration: 3000 });
      } else {
        const data = await res.json();
        toast({ title: data.error || 'Error deleting monitor', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting monitor', status: 'error', duration: 3000 });
    }
    onDeleteModalClose();
    setMonitorToDelete(null);
  };

  const getStatusBadge = (status, lastStatus) => {
    if (status === null) {
      return <Badge colorScheme="gray">Unknown</Badge>;
    }
    if (status === true && lastStatus >= 200 && lastStatus < 400) {
      return <Badge colorScheme="green"><FiCheckCircle style={{ display: 'inline', marginRight: 4 }} />Up</Badge>;
    }
    return <Badge colorScheme="red"><FiXCircle style={{ display: 'inline', marginRight: 4 }} />Down</Badge>;
  };

  const upCount = monitors.filter(m => m.status === true && m.last_status >= 200 && m.last_status < 400).length;
  const downCount = monitors.filter(m => m.status === false || (m.last_status && (m.last_status < 200 || m.last_status >= 400))).length;
  const unknownCount = monitors.filter(m => m.status === null).length;

  return (
    <Box>
      <Flex align="center" mb={6}>
        <Heading size="lg">Monitors</Heading>
        <Spacer />
        <HStack spacing={3}>
          <Button leftIcon={<FiRefreshCw />} onClick={fetchMonitors} isLoading={loading}>
            Refresh
          </Button>
          <Button leftIcon={<FiPlus />} colorScheme="green" onClick={openAddMonitor}>
            Add Monitor
          </Button>
        </HStack>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Stat>
            <StatLabel color="gray.500">Total Monitors</StatLabel>
            <StatNumber fontSize="3xl">{monitors.length}</StatNumber>
            <StatHelpText>Configured endpoints</StatHelpText>
          </Stat>
        </Box>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderLeftColor="green.400">
          <Stat>
            <StatLabel color="gray.500">Up</StatLabel>
            <StatNumber fontSize="3xl" color="green.500">{upCount}</StatNumber>
            <StatHelpText>Healthy endpoints</StatHelpText>
          </Stat>
        </Box>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" borderLeft="4px solid" borderLeftColor="red.400">
          <Stat>
            <StatLabel color="gray.500">Down</StatLabel>
            <StatNumber fontSize="3xl" color="red.500">{downCount}</StatNumber>
            <StatHelpText>Unhealthy endpoints</StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>

      {/* Monitors Table */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        {loading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="green.500" />
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>URL</Th>
                <Th>Description</Th>
                <Th>Status</Th>
                <Th>Response Code</Th>
                <Th>Last Check</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {monitors.map(monitor => (
                <Tr key={monitor.id}>
                  <Td fontWeight="medium" maxW="300px" isTruncated>{monitor.url}</Td>
                  <Td>{monitor.description || '-'}</Td>
                  <Td>{getStatusBadge(monitor.status, monitor.last_status)}</Td>
                  <Td>
                    <Badge colorScheme={monitor.last_status >= 200 && monitor.last_status < 400 ? 'green' : 'red'}>
                      {monitor.last_status || 'N/A'}
                    </Badge>
                  </Td>
                  <Td>{monitor.updated_at ? new Date(monitor.updated_at).toLocaleString() : '-'}</Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<FiEdit2 />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => openEditMonitor(monitor)}
                        aria-label="Edit monitor"
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => confirmDeleteMonitor(monitor)}
                        aria-label="Delete monitor"
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {monitors.length === 0 && !loading && (
          <Text textAlign="center" py={8} color="gray.500">
            No monitors configured. Add one to start monitoring!
          </Text>
        )}
      </Box>

      {/* Monitor Modal */}
      <Modal isOpen={isMonitorModalOpen} onClose={onMonitorModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingMonitor ? 'Edit Monitor' : 'Add Monitor'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired mb={4}>
              <FormLabel>URL</FormLabel>
              <Input
                value={monitorForm.url}
                onChange={(e) => setMonitorForm({ ...monitorForm, url: e.target.value })}
                placeholder="https://example.com/health"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={monitorForm.description}
                onChange={(e) => setMonitorForm({ ...monitorForm, description: e.target.value })}
                placeholder="Enter a description for this monitor"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onMonitorModalClose}>Cancel</Button>
            <Button colorScheme="green" onClick={handleSaveMonitor}>
              {editingMonitor ? 'Save Changes' : 'Create Monitor'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Monitor</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete the monitor for <strong>{monitorToDelete?.url}</strong>?
            This will also delete all history data.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDeleteMonitor}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Monitors;
